import { zodResolver } from '@hookform/resolvers/zod';
import { parseISO, startOfMonth } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { isSupabaseError } from '../../../../constants/supabaseErrors';
import { ExpenseDatabase } from '../../../../types';
import { useSaveExpense } from '../../api/useSaveExpense';
import { toExpenseMonth } from '../../utils/expenseStats';

const formSchema = z.object({
  categoryId: z.string().min(1, 'Choisissez une catégorie'),
  amount: z
    .string()
    .trim()
    .min(1, 'Indiquez le montant')
    .refine((value) => Number(value.replace(',', '.')) > 0, 'Le montant doit être supérieur à 0'),
  month: z.date({ error: 'Choisissez le mois' }).nullable().refine((value) => value !== null, 'Choisissez le mois'),
});

type FormInput = z.input<typeof formSchema>;
type FormValues = z.output<typeof formSchema>;

type Props = {
  onClose: () => void;
  expense?: ExpenseDatabase;
  defaultCategoryId?: string;
};

export function useExpenseFormDialog(props: Props) {
  const { mutateAsync, isPending } = useSaveExpense();
  const [serverError, setServerError] = useState<string | null>(null);
  const isEdition = !!props.expense;

  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      categoryId: props.expense?.category_id ?? props.defaultCategoryId ?? '',
      amount: props.expense ? String(props.expense.amount) : '',
      month: props.expense ? parseISO(props.expense.month) : startOfMonth(new Date()),
    },
  });

  async function handleSubmit(values: FormValues) {
    setServerError(null);

    try {
      await mutateAsync({
        expenseId: props.expense?.id,
        categoryId: values.categoryId,
        amount: Number(values.amount.replace(',', '.')),
        month: toExpenseMonth(values.month),
      });
      props.onClose();
    } catch (error: unknown) {
      setServerError(isSupabaseError(error) ? error.message : 'La dépense n\'a pas pu être enregistrée. Réessayez.');
    }
  }

  function handleClose() {
    if (!isPending) {
      props.onClose();
    }
  }

  return {
    form,
    isEdition,
    onSubmit: form.handleSubmit(handleSubmit),
    isPending,
    serverError,
    handleClose,
  };
}
